import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Description, ErrorMessage, Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { RadioCards } from '../catalyst/radio-cards'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { useAuth } from '../lib/auth'
import { formErrors, noErrors, type FormErrors } from '../lib/form-errors'
import { useFarms } from '../lib/farms'
import { NameInput } from '../lib/name-input'
import { CATEGORY_LABEL, createAsset, inventoryPath, useAsset, useAssetOptions, type AssetInput } from '../lib/inventory'
import type { Asset, AssetCategory } from '../lib/types'
import { Shell } from './shell'

// Adding or editing one piece of equipment or one building, as a page of its own (not a dialog), so
// it works on a phone with the keyboard up and Back does what it should.

type Where = 'MOVABLE' | 'STATIONARY'

function AssetForm({
  farmId,
  farmName,
  initial,
  initialCategory,
  cancelHref,
  onSave,
  onDone,
}: {
  farmId: string
  farmName: string
  initial?: Asset
  initialCategory: AssetCategory
  cancelHref: string
  onSave: (input: AssetInput) => Promise<Asset>
  /** Leave the form: after a save (with the saved asset) or on Cancel (with nothing). */
  onDone: (saved?: Asset) => void
}) {
  const { org } = useAuth()
  const options = useAssetOptions(org?.id)
  const [category, setCategory] = useState<AssetCategory>(initial?.category ?? initialCategory)
  const [where, setWhere] = useState<Where>(initial ? initial.kind : 'MOVABLE')
  const [name, setName] = useState(initial?.name ?? '')
  const [type, setType] = useState(initial?.type ?? '')
  const [makeModel, setMakeModel] = useState(initial?.makeModel ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>(noErrors)

  const movable = category === 'EQUIPMENT' && where === 'MOVABLE'
  const types = options?.types[category]
  const typeSuggestions = movable ? (types?.movable ?? []) : (types?.stationary ?? [])
  const label = CATEGORY_LABEL[category].singular
  const invalid = (key: string) => key in errors.fields
  const message = (key: string) => (invalid(key) ? <ErrorMessage>{errors.fields[key]}</ErrorMessage> : null)

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!type.trim()) return
    setSubmitting(true)
    setErrors(noErrors)
    try {
      const saved = await onSave({
        category,
        farmId: movable ? null : farmId,
        name: name.trim() || type.trim(),
        type: type.trim(),
        makeModel: category === 'EQUIPMENT' ? makeModel.trim() || null : null,
        description: description.trim() || null,
      })
      onDone(saved)
    } catch (err) {
      setErrors(formErrors(err, `Could not save the ${label}.`))
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      {errors.general && <Text className="mb-6 text-red-600 dark:text-red-400">{errors.general}</Text>}
      <FieldGroup>
        {initial ? (
          <Text>
            {CATEGORY_LABEL[category].singular === 'building' ? 'Building' : 'Equipment'} ·{' '}
            {initial.kind === 'MOVABLE' ? 'moves between all your farms' : `at ${farmName}`}. This can't change once added.
          </Text>
        ) : (
          <>
            <RadioCards<AssetCategory>
              label="What are you adding?"
              layout="wrap"
              value={category}
              options={[
                { value: 'EQUIPMENT', label: 'Equipment' },
                { value: 'BUILDING', label: 'Building' },
              ]}
              onChange={setCategory}
            />
            {category === 'EQUIPMENT' && (
              <RadioCards<Where>
                label="Where is it used?"
                value={where}
                options={[
                  { value: 'MOVABLE', label: 'Moves between farms', detail: 'Tractor, sprayer' },
                  { value: 'STATIONARY', label: `Fixed at ${farmName}`, detail: 'Pump, packing line' },
                ]}
                onChange={setWhere}
              />
            )}
          </>
        )}

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
          <FormField>
            <Label>Type</Label>
            <NameInput
              suggestions={typeSuggestions}
              value={type}
              onChange={(e) => setType(e.target.value)}
              invalid={invalid('type')}
              placeholder={category === 'BUILDING' ? 'e.g. Packinghouse' : 'e.g. Tractor'}
              required
              autoFocus={!!initial}
            />
            {message('type')}
          </FormField>
          <FormField>
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              invalid={invalid('name')}
              placeholder={type || (category === 'BUILDING' ? 'e.g. Main packing shed' : 'e.g. Tractor 2')}
            />
            <Description>What your crew calls it. Defaults to the type.</Description>
            {message('name')}
          </FormField>
        </div>
        {category === 'EQUIPMENT' && (
          <FormField>
            <Label>Make and model</Label>
            <Input value={makeModel} onChange={(e) => setMakeModel(e.target.value)} invalid={invalid('makeModel')} />
            {message('makeModel')}
          </FormField>
        )}
        <FormField>
          <Label>Description</Label>
          <Description>
            {category === 'BUILDING' ? 'Anything worth noting: size, what it is used for, where it is.' : 'Anything worth noting: tank size, horsepower, serial number, where it is kept.'}
          </Description>
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} invalid={invalid('description')} />
          {message('description')}
        </FormField>
      </FieldGroup>

      {/* Sticky on phones so Save stays in reach with the keyboard up; inline from sm: up. */}
      <div className="sticky bottom-0 -mx-6 mt-10 flex justify-end gap-3 border-t border-zinc-950/10 bg-white px-6 py-4 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 dark:border-white/10 dark:bg-zinc-900 sm:dark:bg-transparent">
        <Button plain href={cancelHref} onClick={(e: React.MouseEvent) => (e.preventDefault(), onDone())}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : initial ? 'Save' : `Add ${label}`}
        </Button>
      </div>
    </form>
  )
}

export function AssetFormPage() {
  const { farmId, assetId } = useParams<{ farmId: string; assetId?: string }>()
  const [params] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { org } = useAuth()
  const { getFarm, loading: farmsLoading } = useFarms()
  const { asset, loading, error, updateAsset } = useAsset(org?.id, assetId)
  const canWrite = org?.role !== 'AGRONOMIST'

  const farm = farmId ? getFarm(farmId) : undefined
  if (!farmsLoading && !farm) return <Navigate to="/" replace />
  if (!farm || !org || !farmId) return null

  const queryCategory: AssetCategory = params.get('category') === 'BUILDING' ? 'BUILDING' : 'EQUIPMENT'
  if (!canWrite) return <Navigate to={assetId ? `/farms/${farmId}/inventory/${assetId}` : inventoryPath(farmId, queryCategory)} replace />

  const editing = !!assetId
  const back = editing ? `/farms/${farmId}/inventory/${assetId}` : inventoryPath(farmId, queryCategory)

  // Leave the form without leaving it in history, so Back from where you land never reopens it.
  // Cancel, or saving an edit, steps back to the page the form was opened from (the edited asset's
  // page reloads the asset) rather than stacking a second copy of it. Adding lands on the new asset's
  // page in place of the form. `location.key === 'default'` means the form was opened directly, with
  // nothing to step back to.
  const done = (saved?: Asset) => {
    if ((editing || !saved) && location.key !== 'default') navigate(-1)
    else navigate(saved ? `/farms/${farmId}/inventory/${saved.id}` : back, { replace: true })
  }

  return (
    <Shell>
      <Link href={back} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {editing ? (asset?.name ?? 'Back') : 'Equipment and buildings'}
      </Link>

      <div className="mx-auto max-w-2xl">
        {!editing ? (
          <>
            <Heading className="mt-4">Add equipment or a building</Heading>
            <Text>One entry per physical machine or building, so its maintenance and inspections can be logged against it.</Text>
            <AssetForm farmId={farmId} farmName={farm.name} initialCategory={queryCategory} cancelHref={back} onSave={(input) => createAsset(org.id, input)} onDone={done} />
          </>
        ) : loading ? (
          <Text className="mt-6">Loading…</Text>
        ) : !asset || asset.archivedAt ? (
          <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>{error ?? (asset?.archivedAt ? 'This has been archived and can no longer be edited.' : 'Not found.')}</Text>
          </div>
        ) : (
          <>
            <Heading className="mt-4">Edit {asset.name}</Heading>
            <AssetForm key={asset.id} farmId={farmId} farmName={farm.name} initial={asset} initialCategory={asset.category} cancelHref={back} onSave={updateAsset} onDone={done} />
          </>
        )}
      </div>
    </Shell>
  )
}
